import React, { useState, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Swal from 'sweetalert2';

const base_URL = process.env.REACT_APP_API_BASE_URL;
const timeout = process.env.REACT_APP_TIMEOUT ? parseInt(process.env.REACT_APP_TIMEOUT, 10) : 300000; // default 5 minutes`

// Axios instance
const api = axios.create({
  baseURL: base_URL, // where Bal service runs
  timeout: timeout, // 5 minutes timeout
  headers: { 'Content-Type': 'application/json' }, // we are sending JSON
});

// Define types
interface SheetData {
  sheetUrl: string;
  sheetName?: string;
  context: string;
  questionColumn: string;
  answerColumn: string;
  startRow: string;
  endRow: string;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
  skipped?: boolean;
  alreadyProcessedCount?: number;
  skippedCount?: number;
  count?: number;
}

interface SheetInfo {
  title: string;
  sheetId: number;
}

interface SheetNamesResponse {
  success: boolean;
  sheets: SheetInfo[];
  message?: string;
}

// API calls
const processSheet = async (sheetData: SheetData) => {
  const response = await api.post('/RFP_BI/process', {
    sheetUrl: sheetData.sheetUrl,
    sheetName: sheetData.sheetName,
    context: sheetData.context,
    questionColumn: sheetData.questionColumn,
    answerColumn: sheetData.answerColumn,
    startRow: parseInt(sheetData.startRow, 10),
    endRow: parseInt(sheetData.endRow, 10),
  });
  return response.data;
};

const fetchSheetNames = async (sheetUrl: string): Promise<SheetNamesResponse> => {
  const response = await api.get('/RFP_BI/getSheetNames', {
    params: { sheetUrl: sheetUrl }
  });
  return response.data;
};

function App() {
  const [formData, setFormData] = useState({
    sheetUrl: '',
    sheetName: '',
    context: '',
    questionColumn: '',
    answerColumn: '',
    startRow: '',
    endRow: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [restarting, setRestarting] = useState(false);
  const [sheetNames, setSheetNames] = useState<SheetInfo[]>([]);
  const [loadingSheetNames, setLoadingSheetNames] = useState(false);

  const [showInfoPanel, setShowInfoPanel] = useState(false);

  const submittedRef = useRef(false); // prevent multiple submissions
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentSheetUrlRef = useRef<string>('');

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle sheet URL change and fetch sheet names
  const handleSheetUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, sheetUrl: value, sheetName: '' }));
    setSheetNames([]);
    setError(null);

    // Only fetch if URL is not empty and looks like a valid Google Sheets URL
    if (value && value.includes('docs.google.com/spreadsheets')) {
      setLoadingSheetNames(true);
      try {
        const response = await fetchSheetNames(value);
        if (response.success && response.sheets) {
          setSheetNames(response.sheets);
          // Auto-select first sheet if available
          if (response.sheets.length > 0) {
            setFormData(prev => ({ ...prev, sheetName: response.sheets[0].title }));
          }
        } else {
          setError(response.message || 'Failed to fetch sheet names');
        }
      } catch (err: unknown) {
        const error = err as AxiosError;
        const errorMessage = (error.response?.data as { message?: string })?.message || 'Failed to fetch sheet names';
        setError(errorMessage);
      } finally {
        setLoadingSheetNames(false);
      }
    }
  };

  // Poll for completion when request is skipped
  const startPolling = (sheetUrl: string) => {
    currentSheetUrlRef.current = sheetUrl;
    setStatusMessage('Sheet is being processed... Please wait.');
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        // Try to submit again to see if processing is complete
        const data = await processSheet(formData);
        
        // If not skipped anymore, processing is complete
        if (!data?.skipped) {
          stopPolling();
          
          if (data.success) {
            setStatusMessage('');
            const detailsText = [];
            if (data.count !== undefined) {
              detailsText.push(`<div class="swal-detail-item"><span class="swal-detail-number">Newly filled : ${data.count}</span><br><small class="swal-detail-description">Answers were added this time</small></div>`);
            }
            if (data.alreadyProcessedCount !== undefined) {
              detailsText.push(`<div class="swal-detail-item"><span class="swal-detail-number">Already filled answers : ${data.alreadyProcessedCount}</span><br><small class="swal-detail-description">Answers already existed, so no changes made</small></div>`);
            }
            if (data.skippedCount !== undefined) {
              detailsText.push(`<div class="swal-detail-item"><span class="swal-detail-number">Skipped : ${data.skippedCount}</span><br><small class="swal-detail-description">These questions had no text, so they were ignored</small></div>`);
            }
            
            Swal.fire({
              icon: "success",
              title: "Service Successful!",
              html: detailsText.length > 0 ? 
                ` <p class="swal-success-message">
                    Your Google Sheet has been updated successfully.
                  </p>
                  <div class="swal-processing-summary">
                    <h6 class="swal-summary-title">
                      Processing Summary
                    </h6>
                   ${detailsText.join('')}
                 </div>` : 
                  "Your Google Sheet has been updated successfully.",
              confirmButtonColor: "#3085d6",
            });
          } else {
            throw new Error(data.message || "Processing failed");
          }
          
          // Reset states
          submittedRef.current = false;
          setLoading(false);
        }
        // If still skipped, continue polling
        
      } catch (err: unknown) {
        // If there's an error, stop polling and show error
        stopPolling();
        const error = err as AxiosError;
        setError((error.response?.data as { message?: string })?.message || error.message || "An error occurred");
        setStatusMessage('');
        submittedRef.current = false;
        setLoading(false);
      }
    }, 20000); // Poll every 20 seconds
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setStatusMessage('');
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittedRef.current) return; // already processing

    submittedRef.current = true;
    setLoading(true);
    setError(null);
    setStatusMessage('');

    let data: ApiResponse | null = null;

    try {
      data = await processSheet(formData);

      // SKIPPED: start polling for completion
      if (data?.skipped) {
        console.log("Skipped silently:", data.message);
        startPolling(formData.sheetUrl);
        // keep button disabled, don't reset loading/submitted
        return;
      }

      // SUCCESS
      if (data && data.success) {
        const detailsText = [];
        if (data.alreadyProcessedCount !== undefined) {
          detailsText.push(`<div class="swal-detail-item"><span class="swal-detail-number">Already filled answers: ${data.alreadyProcessedCount}</span><br><small class="swal-detail-description">Answers already existed, so no changes made</small></div>`);
        }
        
        if (data.count !== undefined) {
          detailsText.push(`<div class="swal-detail-item"><span class="swal-detail-number">Newly filled: ${data.count}</span><br><small class="swal-detail-description">Answers were added this time</small></div>`);
        }
        
        if (data.skippedCount !== undefined) {
          detailsText.push(`<div class="swal-detail-item"><span class="swal-detail-number">Skipped: ${data.skippedCount}</span><br><small class="swal-detail-description">These questions had no text, so they were ignored</small></div>`);
        }
        
        Swal.fire({
          icon: "success",
          title: "Your Google Sheet has been updated successfully!",
          html: detailsText.length > 0 ? 
            `<div class="swal-processing-summary">
               <h6 class="swal-summary-title">📊 Processing Summary</h6>
               ${detailsText.join('')}
             </div>` : 
            "Your Google Sheet has been updated successfully.",
          confirmButtonColor: "#3085d6",
        });
      } else {
        throw new Error((data && data.message) || "Processing failed");
      }
    } catch (err: unknown) {
        const error = err as AxiosError;
        const backendErrorMessage = (error.response?.data as { message?: string })?.message || error.message || "An unknown error occurred.";

        if (backendErrorMessage.includes("INVALID_ARGUMENT") || backendErrorMessage.includes("Unable to parse range")) {
          setError("Processing Failed. Please check that the Sheet Details are correct.");
        } else {
          setError(backendErrorMessage);
        }

      submittedRef.current = false;
      setLoading(false);
    } finally {
      // Reset button/loading **only if it was a success or failure**, skipped keeps it disabled
      if (!(data?.skipped)) {
        submittedRef.current = false;
        setLoading(false);
      }
    }
  };

  // Handler function for restarting the service
const handleRestart = async () => {
  setRestarting(true);
  try {
    const response = await api.post('/RFP_BI/restart');

    // Show a success message from the backend
    Swal.fire({
      icon: "success",
      title: "Service Restarted!",
      text: response.data.message, // Use the dynamic message from the backend
    });

  } catch (err: unknown) {
    const error = err as AxiosError;
    const errorMessage = (error.response?.data as { message?: string })?.message || "Failed to restart the service.";
    // Show an error message
    Swal.fire({
      icon: "error",
      title: "Restart Failed",
      text: errorMessage,
    });
  } finally {
    setRestarting(false);
  }
};

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          {/* Floating Info Toggle Button */}
          <div className="floating-info-toggle">
            <button
              className="btn btn-info btn-sm shadow-sm"
              onClick={() => setShowInfoPanel(!showInfoPanel)}
              type="button"
            >
              {showInfoPanel ? '✕':'?'}
            </button>
          </div>

          {/* Expandable Info Panel */}
          <div className={`info-panel ${showInfoPanel ? 'show' : ''}`}>
            <div className="card shadow-sm border-info">
              <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Information & Guidelines</h6>
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => setShowInfoPanel(false)}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="card-body p-3">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="info-card">
                      <div className="d-flex align-items-start">
                        <div className="info-icon bg-primary me-3">📋</div>
                        <div>
                          <h6 className="fw-bold mb-2">Requirements</h6>
                          <ul className="small mb-0">
                            <li>Share your Google Sheet with <strong>tharushika@wso2.com</strong> (edit access)</li>
                            <li>Ensure questions are in the specified column</li>
                            <li>Answer column can be empty or have existing content</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <div className="info-card">
                      <div className="d-flex align-items-start">
                        <div className="info-icon bg-success me-3">⚙️</div>
                        <div>
                          <h6 className="fw-bold mb-2">How it Works</h6>
                          <ul className="small mb-0">
                            <li><strong>New answers:</strong> Empty cells get AI responses</li>
                            <li><strong>Existing answers:</strong> Preserved (no overwriting)</li>
                            <li><strong>Empty questions:</strong> Automatically skipped</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm p-4">
            <h3 className="text-center mb-3"> RFP Response Generation Tool</h3>
            <p className="text-muted small text-center">
              Make sure your Google Sheet is shared with <b>tharushika@wso2.com</b> (edit access).
            </p>
            <hr></hr>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Sheet URL</label>
                <input
                  type="text"
                  name="sheetUrl"
                  value={formData.sheetUrl}
                  onChange={handleSheetUrlChange}
                  className="form-control"
                  placeholder="sheets url"
                  required
                />
                {loadingSheetNames && (
                  <div className="text-muted small mt-1">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading sheet names...
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Sheet Name</label>
                {sheetNames.length > 0 ? (
                  <select
                    name="sheetName"
                    value={formData.sheetName}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a sheet</option>
                    {sheetNames.map((sheet) => (
                      <option key={sheet.sheetId} value={sheet.title}>
                        {sheet.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="sheetName"
                    value={formData.sheetName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Sheet1"
                    disabled={loadingSheetNames}
                  />
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Context</label>
                <input
                  type="text"
                  name="context"
                  value={formData.context}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="EX : WSO2 APIM point of view"
                />
              </div>
              <div className="row">
                <div className="col-6 mb-3">
                  <label htmlFor="questionColumnSelect" className="form-label">Question Column</label>
                  <select
                    name="questionColumn"
                    id="questionColumnSelect"
                    value={formData.questionColumn}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="" disabled>Select a letter</option>
                    {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(letter => (
                      <option key={letter} value={letter}>
                        {letter}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 mb-3">
                  <label htmlFor="answerColumnSelect" className="form-label">Answer Column</label>
                  <select
                    name="answerColumn"
                    id="answerColumnSelect"
                    value={formData.answerColumn}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="" disabled>Select a letter</option>
                    {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(letter => (
                      <option key={letter} value={letter}>
                      {letter}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row">
                <div className="col-6 mb-3">
                  <label className="form-label">Start Row</label>
                  <input
                    type="number"
                    name="startRow"
                    value={formData.startRow}
                    onChange={handleChange}
                    className="form-control"
                    required
                    min={1}
                  />
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label">End Row</label>
                  <input
                    type="number"
                    name="endRow"
                    value={formData.endRow}
                    onChange={handleChange}
                    className="form-control"
                    required
                    min={1}
                  />
                </div>
              </div>
              <br></br>
              <div className="d-grid">
                <button type="submit" className="submit-button" disabled={loading || loadingSheetNames}>
                  {loading ? 'Processing...' : ' Generate Responses'}
                </button>
              </div>
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleRestart}
                  disabled={loading || restarting || loadingSheetNames}
                >
                  {restarting ? 'Restarting...' : 'Restart Service'}
                </button>
              </div>
            </form>

            {statusMessage && (
              <div className="alert alert-info mt-3" role="alert">
                {statusMessage}
              </div>
            )}

            {error && (
              <div className="alert alert-danger mt-3" role="alert">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;